import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { 
  Users, LogOut, Home, Bell, Check, X, Calendar,
  Clock, MapPin, ChevronRight, UserPlus, School,
  CheckCircle, XCircle, AlertCircle, Trash2, BellRing
} from "lucide-react";
import { toast, Toaster } from "sonner";
import { requestNotificationPermission, onForegroundMessage } from "@/lib/firebase";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : "/api";

const TIPOS_AVISO_ICONS = {
  treino: "⚽",
  jogo: "🏆",
  evento: "🎉",
  aviso: "📢",
  cancelamento: "❌",
  reuniao: "👥"
};

export default function Responsavel() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [activeTab, setActiveTab] = useState("inicio");
  
  // Auth
  const [token, setToken] = useState(null);
  const [showLogin, setShowLogin] = useState(true);
  
  // Login form
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  
  // Cadastro form
  const [registerNome, setRegisterNome] = useState("");
  const [registerEmail, setRegisterEmail] = useState("");
  const [registerTelefone, setRegisterTelefone] = useState("");
  const [registerPassword, setRegisterPassword] = useState("");
  const [registerCodigo, setRegisterCodigo] = useState("");
  
  // Vincular filho
  const [codigoVinculo, setCodigoVinculo] = useState("");
  
  // Dashboard data
  const [responsavel, setResponsavel] = useState(null);
  const [filhos, setFilhos] = useState([]);
  const [avisos, setAvisos] = useState([]);
  const [avisosNaoLidos, setAvisosNaoLidos] = useState(0);
  
  // Push notifications
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushToken, setPushToken] = useState(null);

  useEffect(() => {
    const savedToken = localStorage.getItem('responsavel_token');
    if (savedToken) {
      setToken(savedToken);
      setIsLoggedIn(true);
      fetchDashboard(savedToken);
      // Verificar se já tem permissão de notificação
      if ('Notification' in window && Notification.permission === 'granted') {
        setPushEnabled(true);
      }
    } else {
      setLoading(false);
    }
  }, []);

  // Setup para receber notificações em foreground
  useEffect(() => {
    if (isLoggedIn && pushEnabled) {
      const unsubscribe = onForegroundMessage((payload) => {
        // Mostrar toast quando receber notificação com app aberto
        toast.info(payload.notification?.title || 'Nova notificação', {
          description: payload.notification?.body,
          duration: 5000
        });
        // Atualizar lista de avisos
        fetchDashboard(token);
      });
      return () => unsubscribe();
    }
  }, [isLoggedIn, pushEnabled, token]);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${token}` }
  });

  const fetchDashboard = async (authToken) => {
    try {
      const response = await axios.get(`${API}/responsavel/dashboard`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setResponsavel(response.data.responsavel);
      setFilhos(response.data.filhos);
      setAvisos(response.data.avisos);
      setAvisosNaoLidos(response.data.avisos_nao_lidos);
      setLoading(false);
    } catch (error) {
      console.error("Erro ao buscar dashboard:", error);
      if (error.response?.status === 401) {
        logout();
      }
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/responsavel/login`, {
        email: loginEmail,
        password: loginPassword
      });
      const newToken = response.data.token;
      localStorage.setItem('responsavel_token', newToken);
      setToken(newToken);
      setIsLoggedIn(true);
      fetchDashboard(newToken);
      toast.success("Login realizado com sucesso!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao fazer login");
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/responsavel/register`, {
        nome: registerNome,
        email: registerEmail,
        telefone: registerTelefone,
        password: registerPassword,
        codigo_vinculo: registerCodigo
      });
      const newToken = response.data.token;
      localStorage.setItem('responsavel_token', newToken);
      setToken(newToken);
      setIsLoggedIn(true);
      toast.success(`Cadastro realizado! ${response.data.filho_vinculado} vinculado.`);
      fetchDashboard(newToken);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao cadastrar");
      setLoading(false);
    }
  };

  const vincularFilho = async () => {
    if (!codigoVinculo) {
      toast.error("Digite o código de vinculação");
      return;
    }
    try {
      const response = await axios.post(`${API}/responsavel/vincular-filho`, {
        codigo_vinculo: codigoVinculo
      }, getAuthHeaders());
      toast.success(`${response.data.filho} vinculado com sucesso!`);
      setCodigoVinculo("");
      fetchDashboard(token);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Código inválido");
    }
  };

  const marcarComoLido = async (avisoId) => {
    try {
      await axios.post(`${API}/responsavel/aviso/${avisoId}/lido`, {}, getAuthHeaders());
      setAvisos(prev => prev.map(a => a.id === avisoId ? { ...a, lido: true } : a));
      setAvisosNaoLidos(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Erro ao marcar como lido");
    }
  };

  const confirmarPresenca = async (avisoId, confirmado) => {
    try {
      await axios.post(`${API}/responsavel/aviso/${avisoId}/confirmar?confirmado=${confirmado}`, {}, getAuthHeaders());
      setAvisos(prev => prev.map(a => a.id === avisoId ? { ...a, confirmado: confirmado, lido: true } : a));
      toast.success(confirmado ? "Presença confirmada!" : "Ausência registrada");
    } catch (error) {
      toast.error("Erro ao confirmar presença");
    }
  };

  const arquivarAviso = async (avisoId) => {
    try {
      await axios.delete(`${API}/responsavel/aviso/${avisoId}`, getAuthHeaders());
      setAvisos(prev => prev.filter(a => a.id !== avisoId));
      setAvisosNaoLidos(prev => Math.max(0, prev - 1));
      toast.success("Aviso arquivado");
    } catch (error) {
      toast.error("Erro ao arquivar aviso");
    }
  };

  // Ativar notificações push
  const ativarNotificacoes = async () => {
    try {
      const fcmToken = await requestNotificationPermission();
      
      if (fcmToken) {
        // Salvar token no backend
        await axios.post(`${API}/responsavel/push-token`, {
          token: fcmToken
        }, getAuthHeaders());
        
        setPushToken(fcmToken);
        setPushEnabled(true);
        toast.success("Notificações ativadas! Você receberá avisos mesmo com o app fechado.");
      } else {
        toast.error("Não foi possível ativar as notificações. Verifique as permissões do navegador.");
      }
    } catch (error) {
      console.error("Erro ao ativar notificações:", error);
      toast.error("Erro ao ativar notificações");
    }
  };

  const logout = () => {
    localStorage.removeItem('responsavel_token');
    setToken(null);
    setIsLoggedIn(false);
    setResponsavel(null);
    setFilhos([]);
    setAvisos([]);
  };

  // Tela de Login/Cadastro
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-cyan-900 flex items-center justify-center p-4">
        <Toaster position="top-center" richColors />
        
        <Card className="w-full max-w-md bg-white/10 backdrop-blur-lg border-white/20">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mb-4">
              <Users className="h-8 w-8 text-white" />
            </div>
            <CardTitle className="text-2xl text-white">Área do Responsável</CardTitle>
            <CardDescription className="text-blue-200">
              Acompanhe seu filho na escola
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <Tabs value={showLogin ? "login" : "cadastro"} onValueChange={(v) => setShowLogin(v === "login")}>
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="login">Entrar</TabsTrigger>
                <TabsTrigger value="cadastro">Cadastrar</TabsTrigger>
              </TabsList>
              
              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-white">Email</Label>
                    <Input
                      type="email"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      placeholder="seu@email.com"
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">Senha</Label>
                    <Input
                      type="password"
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      placeholder="••••••••"
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={loading}>
                    {loading ? "Entrando..." : "Entrar"}
                  </Button>
                </form>
              </TabsContent>
              
              <TabsContent value="cadastro">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-white">Seu Nome</Label>
                    <Input
                      value={registerNome}
                      onChange={(e) => setRegisterNome(e.target.value)}
                      placeholder="Nome completo"
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">Email</Label>
                    <Input
                      type="email"
                      value={registerEmail}
                      onChange={(e) => setRegisterEmail(e.target.value)}
                      placeholder="seu@email.com"
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">Telefone</Label>
                    <Input
                      value={registerTelefone}
                      onChange={(e) => setRegisterTelefone(e.target.value)}
                      placeholder="(11) 99999-9999"
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">Senha</Label>
                    <Input
                      type="password"
                      value={registerPassword}
                      onChange={(e) => setRegisterPassword(e.target.value)}
                      placeholder="••••••••"
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">Código do Filho</Label>
                    <Input
                      value={registerCodigo}
                      onChange={(e) => setRegisterCodigo(e.target.value.toUpperCase())}
                      placeholder="JOAO-XK9A"
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/50 uppercase"
                      required
                    />
                    <p className="text-xs text-blue-300">
                      Solicite o código na secretaria da escola
                    </p>
                  </div>
                  <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={loading}>
                    {loading ? "Cadastrando..." : "Cadastrar"}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Dashboard do Responsável
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-cyan-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-cyan-900">
      <Toaster position="top-center" richColors />
      
      {/* Header */}
      <header className="bg-black/20 backdrop-blur-lg border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
              <Users className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="text-white font-semibold text-sm">{responsavel?.nome}</p>
              <p className="text-blue-300 text-xs">{filhos.length} filho(s)</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {avisosNaoLidos > 0 && (
              <div className="relative">
                <Bell className="h-6 w-6 text-white" />
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {avisosNaoLidos}
                </span>
              </div>
            )}
            <Button variant="ghost" size="icon" className="text-white hover:bg-white/10" onClick={logout}>
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="w-full flex bg-black/20 border border-white/10 mb-6 rounded-lg p-1">
            <TabsTrigger value="inicio" className="flex-1 text-white data-[state=active]:bg-blue-600 py-2 rounded-md flex items-center justify-center gap-2">
              <Home className="h-4 w-4" />
              <span className="hidden sm:inline">Início</span>
            </TabsTrigger>
            <TabsTrigger value="filhos" className="flex-1 text-white data-[state=active]:bg-blue-600 py-2 rounded-md flex items-center justify-center gap-2">
              <Users className="h-4 w-4" />
              <span className="hidden sm:inline">Filhos</span>
            </TabsTrigger>
            <TabsTrigger value="avisos" className="flex-1 text-white data-[state=active]:bg-blue-600 py-2 rounded-md flex items-center justify-center gap-2 relative">
              <Bell className="h-4 w-4" />
              <span className="hidden sm:inline">Avisos</span>
              {avisosNaoLidos > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                  {avisosNaoLidos}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Tab: Início */}
          <TabsContent value="inicio" className="space-y-4">
            {/* Card de Ativar Notificações */}
            {!pushEnabled && (
              <Card className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 border-green-500/30">
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-full bg-green-500/20 flex items-center justify-center">
                      <BellRing className="h-6 w-6 text-green-400" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-white font-semibold">Ativar Notificações</h3>
                      <p className="text-green-300 text-sm">
                        Receba avisos da escola mesmo com o app fechado
                      </p>
                    </div>
                    <Button
                      onClick={ativarNotificacoes}
                      className="bg-green-600 hover:bg-green-700"
                      data-testid="ativar-notificacoes-btn"
                    >
                      Ativar
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
            
            {pushEnabled && (
              <Card className="bg-white/5 border-white/10">
                <CardContent className="p-3">
                  <div className="flex items-center gap-2 text-green-400 text-sm">
                    <CheckCircle className="h-4 w-4" />
                    <span>Notificações ativadas - Você receberá avisos no celular</span>
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Resumo dos Filhos */}
            {filhos.map((filho) => (
              <Card key={filho.id} className="bg-white/10 border-white/20">
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <Avatar className="h-14 w-14 border-2 border-blue-400">
                      <AvatarFallback className="bg-blue-600 text-white text-xl">
                        {filho.nome.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <h3 className="text-white font-semibold">{filho.nome}</h3>
                      <p className="text-blue-300 text-sm flex items-center gap-1">
                        <School className="h-3 w-3" />
                        {filho.escola}
                      </p>
                      {filho.turmas?.length > 0 && (
                        <p className="text-blue-400 text-xs mt-1">
                          {filho.turmas.map(t => t.nome).join(", ")}
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-white">{filho.percentual_presenca}%</p>
                      <p className="text-xs text-blue-300">presença</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            {/* Avisos Recentes */}
            {avisos.filter(a => !a.lido).slice(0, 3).length > 0 && (
              <Card className="bg-gradient-to-r from-orange-500/20 to-red-500/20 border-orange-500/30">
                <CardHeader className="pb-2">
                  <CardTitle className="text-white flex items-center gap-2 text-base">
                    <Bell className="h-5 w-5 text-orange-400" />
                    Avisos Não Lidos
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {avisos.filter(a => !a.lido).slice(0, 3).map((aviso) => (
                    <div 
                      key={aviso.id} 
                      className="bg-white/10 rounded-lg p-3 cursor-pointer hover:bg-white/15"
                      onClick={() => {
                        setActiveTab("avisos");
                        marcarComoLido(aviso.id);
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-xl">{TIPOS_AVISO_ICONS[aviso.tipo] || "📢"}</span>
                        <div className="flex-1">
                          <p className="text-white font-medium text-sm">{aviso.titulo}</p>
                          <p className="text-blue-300 text-xs truncate">{aviso.mensagem}</p>
                        </div>
                        <ChevronRight className="h-5 w-5 text-gray-400" />
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Vincular Outro Filho */}
            <Card className="bg-white/10 border-white/20">
              <CardHeader className="pb-2">
                <CardTitle className="text-white flex items-center gap-2 text-base">
                  <UserPlus className="h-5 w-5 text-green-400" />
                  Vincular Outro Filho
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2">
                  <Input
                    value={codigoVinculo}
                    onChange={(e) => setCodigoVinculo(e.target.value.toUpperCase())}
                    placeholder="CODIGO-XXXX"
                    className="bg-white/10 border-white/20 text-white uppercase"
                  />
                  <Button onClick={vincularFilho} className="bg-green-600 hover:bg-green-700">
                    Vincular
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab: Filhos */}
          <TabsContent value="filhos" className="space-y-4">
            {filhos.map((filho) => (
              <Card key={filho.id} className="bg-white/10 border-white/20">
                <CardContent className="p-4 space-y-4">
                  <div className="flex items-center gap-4">
                    <Avatar className="h-16 w-16 border-2 border-blue-400">
                      <AvatarFallback className="bg-blue-600 text-white text-2xl">
                        {filho.nome.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <h3 className="text-white font-bold text-lg">{filho.nome}</h3>
                      <p className="text-blue-300 text-sm">{filho.escola}</p>
                    </div>
                  </div>
                  
                  {/* Turmas */}
                  {filho.turmas?.length > 0 && (
                    <div>
                      <p className="text-gray-400 text-xs mb-2">Turmas</p>
                      <div className="flex flex-wrap gap-2">
                        {filho.turmas.map((turma) => (
                          <Badge key={turma.id} className="bg-blue-600">
                            {turma.nome}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Presenças */}
                  <div className="grid grid-cols-3 gap-4 p-3 bg-white/5 rounded-lg">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-green-400">{filho.presencas_mes}</p>
                      <p className="text-xs text-gray-400">Presenças</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-red-400">{filho.faltas_mes}</p>
                      <p className="text-xs text-gray-400">Faltas</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-white">{filho.percentual_presenca}%</p>
                      <p className="text-xs text-gray-400">Frequência</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </TabsContent>

          {/* Tab: Avisos */}
          <TabsContent value="avisos" className="space-y-4">
            {avisos.length === 0 ? (
              <Card className="bg-white/10 border-white/20">
                <CardContent className="py-12 text-center">
                  <Bell className="h-12 w-12 text-gray-500 mx-auto mb-4" />
                  <p className="text-gray-400">Nenhum aviso ainda</p>
                </CardContent>
              </Card>
            ) : (
              avisos.map((aviso) => (
                <Card 
                  key={aviso.id} 
                  className={`border-white/20 ${!aviso.lido ? 'bg-blue-500/20 border-blue-500/30' : 'bg-white/10'}`}
                  onClick={() => !aviso.lido && marcarComoLido(aviso.id)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <span className="text-3xl">{TIPOS_AVISO_ICONS[aviso.tipo] || "📢"}</span>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-white font-semibold">{aviso.titulo}</h3>
                          {!aviso.lido && (
                            <Badge className="bg-blue-500 text-xs">Novo</Badge>
                          )}
                        </div>
                        <p className="text-blue-200 text-sm mb-3">{aviso.mensagem}</p>
                        
                        {/* Detalhes do evento */}
                        <div className="flex flex-wrap gap-3 text-xs text-gray-400 mb-3">
                          {aviso.data_evento && (
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {aviso.data_evento}
                            </span>
                          )}
                          {aviso.horario && (
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {aviso.horario}
                            </span>
                          )}
                          {aviso.local && (
                            <span className="flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              {aviso.local}
                            </span>
                          )}
                        </div>
                        
                        {/* Botões de confirmação */}
                        {aviso.requer_confirmacao && (
                          <div className="flex gap-2">
                            {aviso.confirmado === true ? (
                              <Badge className="bg-green-600 flex items-center gap-1">
                                <CheckCircle className="h-3 w-3" />
                                Presença Confirmada
                              </Badge>
                            ) : aviso.confirmado === false ? (
                              <Badge className="bg-red-600 flex items-center gap-1">
                                <XCircle className="h-3 w-3" />
                                Ausência Registrada
                              </Badge>
                            ) : (
                              <>
                                <Button 
                                  size="sm" 
                                  className="bg-green-600 hover:bg-green-700"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    confirmarPresenca(aviso.id, true);
                                  }}
                                >
                                  <Check className="h-4 w-4 mr-1" />
                                  Confirmar
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="border-red-500 text-red-500 hover:bg-red-500/20"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    confirmarPresenca(aviso.id, false);
                                  }}
                                >
                                  <X className="h-4 w-4 mr-1" />
                                  Não Irei
                                </Button>
                              </>
                            )}
                          </div>
                        )}
                        
                        {/* Botão de arquivar (apenas se o aviso foi lido) */}
                        {aviso.lido && (
                          <div className="mt-3 pt-3 border-t border-white/10">
                            <Button 
                              size="sm" 
                              variant="ghost"
                              className="text-gray-400 hover:text-red-400 hover:bg-red-500/10"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (confirm("Tem certeza que deseja arquivar este aviso?")) {
                                  arquivarAviso(aviso.id);
                                }
                              }}
                            >
                              <Trash2 className="h-4 w-4 mr-1" />
                              Arquivar Aviso
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
